// Image and video uploads go to the separate upload service
// (REACT_APP_MEDIA_SERVER), which parses multipart bodies with busboy 1.x.
// busboy decodes the `filename` parameter as latin1, so a Chinese name such
// as "教案.jpg" reaches that service as mojibake ("æ•™æ¡ˆ.jpg") and is written
// as-is into the S3 key and the `name` of the media document. That service
// is maintained elsewhere and cannot be changed from this repo, so the
// browser never sends a non-ASCII filename to it: the file goes up under an
// ASCII placeholder and the real name is written afterwards through this
// app's own API (PATCH /api/image/:id, PATCH /api/video/:id), which shares
// the database with the upload service.

export const hasNonAscii = (name) =>
  typeof name === "string" && /[^\x20-\x7e]/.test(name);

const splitExtension = (name) => {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return [name, ""];
  return [name.slice(0, dot), name.slice(dot + 1)];
};

// "教案 測試-1.jpg" -> "1.jpg"; "教案.jpg" -> "image.jpg"; "報告.圖" -> "image.bin"
export const asciiPlaceholderName = (name, fallbackBase = "file") => {
  const [base, ext] = splitExtension(String(name || ""));
  const asciiBase = base
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^[_.-]+|[_.-]+$/g, "")
    .slice(0, 80);
  const asciiExt = /^[A-Za-z0-9]{1,10}$/.test(ext) ? ext : "bin";
  return `${asciiBase || fallbackBase}.${asciiExt}`;
};

// Rebuilds a FormData so every file entry whose name is not pure ASCII is
// sent under a placeholder. Returns the (possibly same) FormData plus the
// real names in file-entry order, `null` where nothing was renamed. The
// upload service answers plural endpoints in the same order, so the index
// is enough to match a real name back to its document.
export const asciiFormDataFilenames = (formData) => {
  if (!formData || typeof formData.entries !== "function") {
    return { formData, realNames: [] };
  }
  const entries = [];
  const realNames = [];
  let changed = false;
  for (const [key, value] of formData.entries()) {
    if (value instanceof Blob) {
      const name = typeof value.name === "string" ? value.name : "";
      if (hasNonAscii(name)) {
        changed = true;
        realNames.push(name);
        entries.push([key, value, asciiPlaceholderName(name, key)]);
      } else {
        realNames.push(null);
        entries.push([key, value, name || undefined]);
      }
    } else {
      entries.push([key, value]);
    }
  }
  if (!changed) return { formData, realNames };

  const next = new FormData();
  for (const entry of entries) {
    if (entry.length === 3 && entry[2] !== undefined) {
      next.append(entry[0], entry[1], entry[2]);
    } else {
      next.append(entry[0], entry[1]);
    }
  }
  return { formData: next, realNames };
};
